import { useEffect, useRef } from "react";
import { useMood } from "../lib/mood.ts";
import styles from "./GooglyEyes.module.css";

type Props = {
  size?: "sm" | "lg";
  follow?: boolean;
  className?: string;
};

export function GooglyEyes({ size = "sm", follow = false, className }: Props) {
  const ref = useRef<HTMLSpanElement>(null);
  const mood = useMood();

  useEffect(() => {
    const element = ref.current;
    if (!follow || !element || matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let frame = 0;
    const onMove = (event: PointerEvent) => {
      if (event.pointerType === "touch") return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const box = element.getBoundingClientRect();
        const dx = event.clientX - (box.left + box.width / 2);
        const dy = event.clientY - (box.top + box.height / 2);
        const distance = Math.hypot(dx, dy) || 1;
        const reach = Math.min(distance / 240, 1);
        element.style.setProperty("--look-x", ((dx / distance) * reach).toFixed(3));
        element.style.setProperty("--look-y", ((dy / distance) * reach).toFixed(3));
      });
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("pointermove", onMove);
    };
  }, [follow]);

  return (
    <span
      ref={ref}
      className={`${styles.blob} ${className ?? ""}`}
      data-size={size}
      data-follow={follow || undefined}
      data-mood={mood === "idle" ? undefined : mood}
      aria-hidden="true"
    >
      <span className={styles.eye}>
        <span className={styles.pupil} />
      </span>
      <span className={styles.eye}>
        <span className={styles.pupil} />
      </span>
    </span>
  );
}
